import { cva, type VariantProps } from 'class-variance-authority';
import { Paperclip, X } from 'lucide-react';
import * as React from 'react';
import { formatFileSize } from '../lib/format-file-size';
import { cn } from '../lib/utils';
import { Button } from './button';

export interface FileAttachmentItem {
  id: string;
  name: string;
  size?: number;
  type?: string;
  status: 'uploading' | 'ready' | 'error';
  error?: string;
  url?: string;
}

const fileAttachmentChipVariants = cva('flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs max-w-full', {
  variants: {
    status: {
      uploading: 'bg-soft animate-pulse',
      ready: 'bg-muted',
      error: 'highlight-red',
    },
  },
  defaultVariants: {
    status: 'ready',
  },
});

export interface FileAttachmentChipProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'>,
    VariantProps<typeof fileAttachmentChipVariants> {
  item: FileAttachmentItem;
  /** Called when the chip's remove button is pressed. Omit to hide the button (e.g. sent attachments). */
  onRemove?: (id: string) => void;
  /** Called when the chip itself is pressed (e.g. to download a sent attachment). */
  onClick?: (item: FileAttachmentItem) => void;
  removeLabel?: string;
  uploadingLabel?: string;
}

export const FileAttachmentChip = React.forwardRef<HTMLDivElement, FileAttachmentChipProps>(
  ({ item, onRemove, onClick, removeLabel = 'Remove attachment', uploadingLabel = 'Uploading…', className, ...props }, ref) => {
    const content = (
      <>
        <Paperclip className="size-3.5 shrink-0" />
        <span className="font-medium truncate">{item.name}</span>
        {item.status === 'ready' && typeof item.size === 'number' && (
          <span className="text-muted-foreground shrink-0">({formatFileSize(item.size)})</span>
        )}
        {item.status === 'uploading' && <span className="text-muted-foreground shrink-0">{uploadingLabel}</span>}
      </>
    );

    return (
      <div ref={ref} className={cn(fileAttachmentChipVariants({ status: item.status }), className)} {...props}>
        {onClick ? (
          <button type="button" onClick={() => onClick(item)} className="flex items-center gap-1.5 min-w-0 hover:underline">
            {content}
          </button>
        ) : (
          <span className="flex items-center gap-1.5 min-w-0">{content}</span>
        )}
        {item.status === 'error' && item.error && <span className="text-destructive min-w-0 truncate">{item.error}</span>}
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="size-4 shrink-0 rounded-full"
            onClick={() => onRemove(item.id)}
            aria-label={removeLabel}
          >
            <X className="size-3" />
          </Button>
        )}
      </div>
    );
  }
);

FileAttachmentChip.displayName = 'FileAttachmentChip';

export interface FileAttachmentListProps {
  items: FileAttachmentItem[];
  onRemove?: (id: string) => void;
  onClick?: (item: FileAttachmentItem) => void;
  removeLabel?: string;
  uploadingLabel?: string;
  className?: string;
}

export function FileAttachmentList({ items, onRemove, onClick, removeLabel, uploadingLabel, className }: FileAttachmentListProps) {
  if (items.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {items.map((item) => (
        <FileAttachmentChip
          key={item.id}
          item={item}
          onRemove={onRemove}
          onClick={onClick}
          removeLabel={removeLabel}
          uploadingLabel={uploadingLabel}
        />
      ))}
    </div>
  );
}

export interface AttachFileButtonProps {
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
  label?: string;
  className?: string;
}

export function AttachFileButton({ accept, multiple = true, disabled, onFiles, label = 'Attach file', className }: AttachFileButtonProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      onFiles(Array.from(files));
    }
    event.target.value = '';
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        className="hidden"
        disabled={disabled}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className={cn('rounded-full', className)}
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        aria-label={label}
      >
        <Paperclip className="size-4" />
      </Button>
    </>
  );
}
